import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Injector,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import {Router} from '@angular/router';
import {FrontendDocument, FrontendStatusEnum, NachdigalService, StatusEnum} from '@nachdigal';
import {TAB, TabEnumName, UpdateDocumentService} from '@utils-lib';
import {SearchFilterService} from '../shared/service/search-filter.service';
import {TableHeader} from './tabbar/tab/data-table/data-table.component';
import {Subscription} from 'rxjs';
import {DocumentsMetadataService} from '../shared/service/documents-metadata.service';
import {LoaderService} from '../shared/service/loader.service';

@Component({
    selector: 'frageboegen-uebersicht',
    templateUrl: './uebersicht.component.html',
    styleUrls: ['./uebersicht.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UebersichtComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('uebersichtHeader') uebersichtHeader;
    @ViewChild('searchInput') searchInput;
    public documents: Array<FrontendDocument>;
    public sentDocuments: Array<FrontendDocument>;
    public unsentDocuments: Array<FrontendDocument>;
    public searchValue = '';
    public activeSearch = false;
    public sentDocumentId: string;
    public selectedDocumentId: string;
    public unsentTableHeaders: TableHeader[] = [
        {
            name: 'Beraternr.',
            id: 'consultant_number',
            sortDirection: ''
        },
        {
            name: 'Mandantennr.',
            id: 'master_client_number',
            sortDirection: ''
        },
        {
            name: 'Name',
            id: 'client_name',
            sortDirection: ''
        },
        {
            name: 'Fragebogentyp',
            id: 'app_name',
            sortDirection: ''
        },
        {
            name: 'Zuletzt bearbeitet',
            id: 'last_changed_on',
            sortDirection: ''
        }
    ];
    public sentTableHeaders: TableHeader[] = [
        {
            name: 'Beraternr.',
            id: 'consultant_number',
            sortDirection: ''
        },
        {
            name: 'Mandantennr.',
            id: 'master_client_number',
            sortDirection: ''
        },
        {
            name: 'Name',
            id: 'client_name',
            sortDirection: ''
        },
        {
            name: 'Fragebogentyp',
            id: 'app_name',
            sortDirection: ''
        },
        {
            name: 'Übermittelt am',
            id: 'transfer_date',
            sortDirection: ''
        }
    ];
    public unsentTab: ITabbedDataTable = {
        name: TAB.ENTWUERFE,
        category: [FrontendStatusEnum.Sending, FrontendStatusEnum.SendingConfirmed,
            FrontendStatusEnum.SendingError, FrontendStatusEnum.Created],
        title: 'Fragebogen-Entwürfe',
        count: 0,
        active: true,
        defaultSortColumn: 'last_changed_on'
    };
    public sentTab: ITabbedDataTable = {
        name: TAB.GESENDET,
        category: [FrontendStatusEnum.SendingFinalized, FrontendStatusEnum.SendingSuccessful],
        title: 'übermittelte Fragebögen',
        count: 0,
        active: false,
        defaultSortColumn: 'transfer_date'
    };
    public tabbedTable: ITabbedDataTable[] = [this.unsentTab, this.sentTab];

    private readonly statusIntervals = [];

    private readonly router: Router;
    private readonly searchFilterService: SearchFilterService;
    private readonly nachdigalService: NachdigalService;
    private readonly changeDetection: ChangeDetectorRef;
    private readonly updateDocumentService: UpdateDocumentService;
    private readonly ref: ChangeDetectorRef;
    private readonly loadingService: LoaderService;
    private readonly documentsMetadataService: DocumentsMetadataService;
    private readonly documentService: NachdigalService;

    private statusSubscription: Subscription;
    private initializing = true;

    constructor( private readonly injector: Injector) {
        this.router = injector.get<Router>(Router);
        this.searchFilterService = injector.get<SearchFilterService>(SearchFilterService);
        this.nachdigalService = injector.get<NachdigalService>(NachdigalService);
        this.changeDetection = injector.get<ChangeDetectorRef>(ChangeDetectorRef);
        this.updateDocumentService = injector.get<UpdateDocumentService>(UpdateDocumentService);
        this.ref = injector.get<ChangeDetectorRef>(ChangeDetectorRef);
        this.loadingService = injector.get<LoaderService>(LoaderService);
        this.documentsMetadataService = injector.get<DocumentsMetadataService>(DocumentsMetadataService);
        this.documentService = injector.get<NachdigalService>(NachdigalService);
    }

    public ngOnInit(): void {
        this.loadingService.isLoading = {
            isLoading: true,
            immediate: true,
            timeout: {
                value: 10000,
                errorStatus: 503,
                errorPath: 'unavailable'
            }
        };
        this.documentsMetadataService.getDocuments().then(docs => {
            this.initializing = false;
            this.loadingService.isLoading = {isLoading: false};

            this.documents = docs;
            if (sessionStorage.getItem('searchValue')) {
                this.searchValue = sessionStorage.getItem('searchValue');
                this.activeSearch = true;
            }

            this.sentDocuments =
                this.searchFilterService.getDocuments(TAB.GESENDET, this.documents, this.searchValue, this.activeSearch);
            this.unsentDocuments =
                this.searchFilterService.getDocuments(TAB.ENTWUERFE, this.documents, this.searchValue, this.activeSearch);
            this.setTabCount();
            this.ref.detectChanges();

            this.documents.filter(
                document =>
                    document.status === FrontendStatusEnum.Sending ||
                    document.status === FrontendStatusEnum.SendingConfirmed
            ).forEach(document => {
                const interval = 2000;
                const statusInterval = setInterval(() => {
                    this.nachdigalService.getTaskStatus(document.id).toPromise().then(status => {
                        if (status.status !== StatusEnum.SendingConfirmed) {
                            this.documentService.getFrontendDocument(document.id).subscribe(frondendDocument => {
                                clearInterval(statusInterval);
                                this.updateList(frondendDocument);
                            });
                        }
                    });
                }, interval);
                this.statusIntervals.push(statusInterval);
            });

            this.statusSubscription = this.updateDocumentService.currentDocument.subscribe(document => {
                this.selectedDocumentId = document?.id;
                this.ref.detectChanges();
            });
        });
    }

    public updateList(document: FrontendDocument): void {
        const index = this.findDocumentIndexById(this.documents, document.id);
        this.documents[index] = document;
        if (document.status === FrontendStatusEnum.SendingSuccessful) {
            this.sentDocuments = this.searchFilterService.getDocuments(
                TAB.GESENDET, this.documents, this.searchValue, this.activeSearch
            );
            this.unsentDocuments = this.searchFilterService.getDocuments(
                TAB.ENTWUERFE, this.documents, this.searchValue, this.activeSearch
            );
            this.setTabCount();
            if (document.id === this.selectedDocumentId) {
                this.sentDocumentId = document.id;
                this.setActiveTab({name: TAB.GESENDET});
            }
        } else {
            this.setActiveTab({name: TAB.ENTWUERFE});
        }
        this.ref.detectChanges();
    }


    public ngAfterViewInit(): void {
        const doc = document.documentElement;
        const uebersichtHeaderHeight = this.uebersichtHeader?.nativeElement.offsetHeight;
        if (uebersichtHeaderHeight > 0) {
            doc.style.setProperty('--uebersicht-header-height', `${uebersichtHeaderHeight}px`);
        }
    }

    public ngOnDestroy(): void {
        this.statusIntervals.forEach(interval => {
            clearInterval(interval);
        });
        this.statusSubscription?.unsubscribe();
    }

    public notifiySearch(activeSearch: boolean, searchValue: string): void {
        const tabData = this.setCurrentTabData(searchValue, activeSearch);
        this.sentDocuments = this.searchFilterService.getDocuments(TAB.GESENDET, this.documents, this.searchValue, this.activeSearch);
        this.unsentDocuments = this.searchFilterService.getDocuments(TAB.ENTWUERFE, this.documents, this.searchValue, this.activeSearch);
        this.setTabCount();
        this.searchFilterService.publish(tabData);
    }

    public onTabSelected($event: IDocSelectedEvent): void {
        this.setActiveTab($event);
        const tabData = this.setCurrentTabData(this.searchValue, this.activeSearch);
        this.searchFilterService.publish(tabData);
    }

    public deleteItem(taskId: string): void {
        this.nachdigalService.delete(taskId).subscribe(() => {
            this.documents = this.deleteItemFromArray(this.documents, taskId);
            this.unsentDocuments =
                this.searchFilterService.getDocuments(TAB.ENTWUERFE, this.documents, this.searchValue, this.activeSearch);
            this.setTabCount();
            this.changeDetection.detectChanges();
        }, error => {
            console.error(error);
        });
    }

    public deleteItemFromArray(array: Array<FrontendDocument>, id: string): Array<FrontendDocument> {
        const itemToRemove = array.find(x => x.id === id);
        array.forEach((item, index) => {
            if (item === itemToRemove) {
                array.splice(index, 1);
            }
        });
        return array;
    }

    public findDocumentIndexById(array: Array<FrontendDocument>, id: string): number {
        let foundIndex = -1;
        array.forEach((item, index) => {
            if (item.id === id) {
                foundIndex = index;
            }
        });
        return foundIndex;
    }

    public search(force = false): void {
        this.searchValue = this.searchInput.nativeElement.value;
        const minSearchCharacters = 3;
        if (this.searchValue.length >= minSearchCharacters || force) {
            sessionStorage.setItem('searchValue', this.searchValue);
            this.activeSearch = true;
            this.notifiySearch(this.activeSearch, this.searchValue);
        }
        if (this.searchValue === '') {
            this.deleteSearch();
        }
    }

    public deleteSearch(): void {
        this.activeSearch = false;
        this.searchValue = '';
        sessionStorage.removeItem('searchValue');
        this.notifiySearch(this.activeSearch, this.searchValue);
    }

    private setActiveTab({name}: { name: string }): void {
        this.tabbedTable.forEach(tab => {
            tab.active = name === tab.name;
        });
    }

    private setCurrentTabData(searchValue: string, activeSearch: boolean): FrontendDocument[] {
        const activeTab = this.tabbedTable.find(tab => tab.active);
        return this.searchFilterService.getFilteredDocuments(activeTab.name, this.documents, searchValue, activeSearch);
    }

    private setTabCount(): void {
        this.tabbedTable.forEach(tab => {
            const tabData = this.searchFilterService.getFilteredDocuments(tab.name, this.documents, this.searchValue, this.activeSearch);
            tab.count = tabData.length;
            if (tab.count === 1) {
                tab.title = (tab.name === TAB.GESENDET) ? 'übermittelter Fragebogen' : 'Fragebogen-Entwurf';
            } else {
                tab.title = (tab.name === TAB.GESENDET) ? 'übermittelte Fragebögen' : 'Fragebögen-Entwürfe';
            }
        });
    }

}

export interface ITabbedDataTable {
    name: TabEnumName;
    category: string[];
    title: string;
    count: number;
    active: boolean;
    defaultSortColumn?: string;
}

export interface IDocSelectedEvent {
    name: string;
}
